import * as React from 'react';

import App from 'src/state/App';
import Room from 'src/state/Room';
import RoomPage from 'src/state/RoomPage';
import Game from 'src/state/Game';
import Player from 'src/state/Player';
import { TwinPair, Ref, RefSet, assertType } from 'src/util';

import ModalDialog from 'src/ui/ModalDialog';

export type State = {
  type: 'GameRow',
  players: TwinPair<Ref<Player>>,
  ratings: TwinPair<number> | null,
  scaledRatingDiffs: TwinPair<number> | null,
  selection?: 'win' | 'loss' | 'draw' | 'pause-player-1' | 'pause-player-2' | 'pause-pair',
};

export const Component: React.SFC<{
  store: App.Store,
  localState: State,
  room: Room,
  hidden: boolean,
}> = ({ store, localState, room, hidden }) => {
  const dispatch = store.dispatchCurrentRoomPage;
  const hideDialog = () => RoomPage.hideDialog(dispatch);

  function getName(playerRef: Ref<Player>) {
    const player = RefSet.resolve(room.entities.players, playerRef);
    return player && player.name;
  }

  return (
    <ModalDialog
      className="game-row-dialog"
      title="Select an Option"
      buttons={[
        ['Cancel', hideDialog],
        ['Select', !localState.selection ? null : () => {
          if (!localState.selection) {
            return;
          }

          if (
            localState.selection === 'win' ||
            localState.selection === 'loss' ||
            localState.selection === 'draw'
          ) {
            store.dispatchRoom(room.ref, {
              type: 'add-game',
              data: Game({
                players: localState.players,
                outcome: localState.selection,
              }),
            });
          } else if (
            localState.selection === 'pause-player-1' ||
            localState.selection === 'pause-player-2'
          ) {
            const player = (localState.selection === 'pause-player-1'
              ? localState.players[0]
              : localState.players[1]
            );

            store.dispatchRoom(room.ref, {
              type: 'set-player-paused',
              data: {
                player,
                paused: true,
              },
            });
          } else if (localState.selection === 'pause-pair') {
            store.dispatchRoom(room.ref, {
              type: 'set-player-pair-paused',
              data: {
                playerPair: localState.players,
                paused: true,
              },
            });
          } else {
            assertType<never>(localState.selection);
          }

          hideDialog();
        }],
      ]}
      bodyPadding="0"
      hidden={hidden}
      hideDialog={hideDialog}
    >
      {([0, 1] as (0 | 1)[]).map(side => (
        <div
          className={(
            `option${
              localState.selection === ['win', 'loss'][side] ?
              ' selected' :
              ''
            }`
          )}
          onClick={() => dispatch({
            type: 'set-dialog',
            data: {
              ...localState,
              selection: side === 0 ? 'win' : 'loss',
            }
          })}
          style={{ position: 'relative' }}
        >
          {(() => {
            const { ratings } = localState;

            if (!ratings) {
              return [];
            }

            return (
              <span style={{ position: 'absolute', left: '1em' }}>
                {Math.round(100 * App.winChance(ratings[side], ratings[side === 0 ? 1 : 0]))}%
              </span>
            );
          })()}
          {getName(localState.players[side])} won
          {(() => {
            const { scaledRatingDiffs } = localState;

            if (!scaledRatingDiffs) {
              return [];
            }

            return (
              <span style={{ position: 'absolute', right: '1em' }}>
                +{Math.round(scaledRatingDiffs[side])}
              </span>
            );
          })()}
        </div>
      ))}
      <div
        className={(
          `option${localState.selection === 'draw' ? ' selected' : ''}`
        )}
        onClick={() => dispatch({
          type: 'set-dialog',
          data: {
            ...localState,
            selection: 'draw',
          }
        })}
      >
        Draw
      </div>
      <div
        className={(
          `option${
            localState.selection === 'pause-player-1' ?
            ' selected' :
            ''
          }`
        )}
        onClick={() => dispatch({
          type: 'set-dialog',
          data: {
            ...localState,
            selection: 'pause-player-1',
          }
        })}
      >
        Pause {getName(localState.players[0])}
      </div>
      <div
        className={(
          `option${
            localState.selection === 'pause-player-2' ?
            ' selected' :
            ''
          }`
        )}
        onClick={() => dispatch({
          type: 'set-dialog',
          data: {
            ...localState,
            selection: 'pause-player-2',
          }
        })}
      >
        Pause {getName(localState.players[1])}
      </div>
      <div
        className={(
          `option${
            localState.selection === 'pause-pair' ?
            ' selected' :
            ''
          }`
        )}
        onClick={() => dispatch({
          type: 'set-dialog',
          data: {
            ...localState,
            selection: 'pause-pair',
          }
        })}
      >
        Pause pair
      </div>
    </ModalDialog>
  );
};



// WEBPACK FOOTER //
// ./src/ui/GameRow/Dialog.tsx